import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "./constants";

export const Navigation = ({ scrollTo }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (scrollTo) scrollTo(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "backdrop-blur-md bg-[#181310]/70 border-b border-white/5"
            : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:py-5">
          <button
            onClick={() => go("#top")}
            className="flex items-center"
            data-testid="nav-logo"
            aria-label="DUNC — home"
          >
            <img
              src={LOGO_URL}
              alt="DUNC"
              className="dunc-logo-invert h-6 w-auto md:h-7"
              width="140"
              height="30"
            />
          </button>

          <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="text-[13px] uppercase tracking-[0.22em] text-[#CFC6BC] transition-colors hover:text-[#F5EFE6]"
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => go("#try")}
              data-testid="nav-first-sip-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#D9A441] px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.22em] text-[#181310] transition-colors hover:bg-[#E7B35A]"
            >
              Try DUNC
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#F5EFE6]"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="nav-menu-btn"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 md:hidden bg-[#181310]"
            data-testid="mobile-menu"
          >
            <div className="flex h-full flex-col px-6 pt-24 pb-10">
              <nav className="flex flex-col gap-2">
                {NAV_LINKS.map((l, i) => (
                  <motion.button
                    key={l.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 + i * 0.05 }}
                    onClick={() => go(l.href)}
                    className="py-4 text-left font-display text-3xl font-bold text-[#F5EFE6] border-b border-white/5"
                    data-testid={`mnav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {l.label}
                  </motion.button>
                ))}
              </nav>
              <button
                type="button"
                onClick={() => go("#try")}
                className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-[#D9A441] px-8 py-4 text-sm font-medium uppercase tracking-[0.24em] text-[#181310]"
                data-testid="mnav-first-sip-btn"
              >
                Try DUNC
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
